import { ProfileListItem } from "@/profiles_common/CommonComponents";
import { ADDON_ROW_ID_PREFIX, LINE_ITEM_ROW_ID_PREFIX } from "./constants";
import { internalServiceItems } from "./serviceItemsStore";
import { store } from "./bidStore";

// NOTE: utils for convert object data to grid data
export const getColumns = (columnProfiles) => {
    const columns = [];
    columnProfiles.forEach((columnProfile) => {
        columns.push({
            columnId: columnProfile.id,
            width: columnProfile.width || 110,
        });
    });
    return columns;
};

export const headerRow = (columnProfiles) => {
    const cells = [];
    columnProfiles.forEach((columnProfile) => {
        cells.push({
            type: "text",
            text: columnProfile.name || columnProfile.id,
            renderer: () => (
                <ProfileListItem
                    icon={columnProfile.headerIcon}
                    text={columnProfile.headerText}
                />
            ),
            nonEditable: true,
        });
    });
    return {
        rowId: "header",
        cells: cells,
    };
};

export const getRow = (
    obj,
    columnProfiles,
    hasChildren,
    parentId,
    indent,
    height,
    isExpanded,
    openedDropdown,
    checked,
    customDropdownMappings,
    skipColumnIds,
) => {
    const cells = [];
    columnProfiles.forEach((columnProfile) => {
        const colId = columnProfile.id;
        const colField = columnProfile.isRunning
            ? colId.substring(colId.indexOf(".") + 1)
            : colId;
        const skipColumn = skipColumnIds && skipColumnIds.includes(colId);
        // TODO: push cell by type, don't merge
        if (
            (colField.startsWith("sub_quantities.") ||
                colField in obj ||
                columnProfile.renderer) &&
            !skipColumn
        ) {
            const customDropdownMapping =
                customDropdownMappings && customDropdownMappings[colId];
            const getCustomValue = () => {
                let value = null;
                if (colField.startsWith("sub_quantities.")) {
                    const subKey = colField.split(".")[1];
                    value = obj.sub_quantities
                        ? obj.sub_quantities[subKey] || 0
                        : 0;
                } else {
                    value = obj[colField];
                }
                if (customDropdownMapping) {
                    return customDropdownMapping[value];
                }
                return value;
            };
            const customValue = getCustomValue();
            const cell =
                columnProfile.type == "dropdown"
                    ? {
                          rowId: obj.id,
                          columnId: colId,
                          type: columnProfile.type,
                          selectedValue: customValue,
                          values: columnProfile.values,
                          isDisabled: false,
                          isOpen: openedDropdown
                              ? openedDropdown.rowId == obj.id &&
                                openedDropdown.columnId == colId
                              : false,
                          style: columnProfile.style,
                      }
                    : {
                          rowId: obj.id,
                          columnId: colId,
                          type: columnProfile.type,
                          text: customValue || "",
                          value: customValue || 0,
                          parentId: parentId,
                          isExpanded: isExpanded == null ? false : isExpanded,
                          hasChildren: hasChildren,
                          indent: indent,
                          renderer: columnProfile.renderer,
                          nonEditable: columnProfile.nonEditable,
                          style: { ...columnProfile.style },
                          format: columnProfile.format,
                          checked: checked,
                      };
            if (columnProfile.type == "customDropdownRateType") {
                cell.is_internal = obj.is_internal;
            }
            if (
                columnProfile.type == "dynamicRate" &&
                obj.id.startsWith(LINE_ITEM_ROW_ID_PREFIX)
            ) {
                if (!obj.is_internal || internalServiceItems.length == 0) {
                    cell.type = "simpleFormula";
                } else {
                    const extraData = {
                        currency: obj.vendor_currency,
                        rate_type: obj.rate_type,
                        is_internal: obj.is_internal,
                    };
                    cell.value = obj.service_item
                        ? {
                              ...obj.service_item,
                              ...extraData,
                          }
                        : {
                              name: "",
                              vendor_rate: obj.vendor_rate,
                              ...extraData,
                          };
                }
            }
            if (columnProfile.formatter) {
                cell.format = columnProfile.formatter(
                    store.bid.project?.currency,
                );
            }
            cell.isRunning = columnProfile.isRunning ? true : false;
            cells.push(cell);
        } else {
            cells.push({
                type: "text",
                text: "",
                nonEditable: true,
                style: { ...columnProfile.style },
            });
        }
    });

    return {
        rowId: obj.id,
        reorderable: true,
        cells: cells,
        indent: indent,
        height: height,
        parentId: parentId,
    };
};

export const reorderArray = (arr, idxs, to) => {
    const movedElements = arr.filter((_, idx) => idxs.includes(idx));
    const targetIdx =
        Math.min(...idxs) < to
            ? (to += 1)
            : (to -= idxs.filter((idx) => idx < to).length);
    const leftSide = arr.filter(
        (_, idx) => idx < targetIdx && !idxs.includes(idx),
    );
    const rightSide = arr.filter(
        (_, idx) => idx >= targetIdx && !idxs.includes(idx),
    );
    return [...leftSide, ...movedElements, ...rightSide];
};

// NOTE: Other utils

export const shuffle = (array) => {
    let currentIndex = array.length;
    while (currentIndex != 0) {
        let randomIndex = Math.floor(Math.random() * currentIndex);
        currentIndex--;
        [array[currentIndex], array[randomIndex]] = [
            array[randomIndex],
            array[currentIndex],
        ];
    }
};

export const isRowFullyExpanded = (rowsHash, row) => {
    let currentRow = row;
    let chevron = getChevronCellFromRow(currentRow);
    if (!chevron) {
        return true;
    }
    while (currentRow && chevron && chevron.parentId != null) {
        currentRow = rowsHash[chevron.parentId];
        chevron = getChevronCellFromRow(currentRow);
        if (!chevron.isExpanded) {
            return false;
        }
    }
    return true;
};

export const getVisibleRows = (rows) => {
    const rowsHash = rowsToHashTable(rows);
    return rows.filter((row) => isRowFullyExpanded(rowsHash, row));
};

export const getSearchedLineItemRows = (rows, searchString) => {
    const visibleGroupsSet = new Set();
    if (!searchString) {
        return rows;
    }
    const searchedRows = rows.filter((row, index) => {
        if (index == 0) {
            return true;
        }
        const [rowTypePrefix, lineId] = row.rowId.split("-");
        if (
            !(
                rowTypePrefix == LINE_ITEM_ROW_ID_PREFIX ||
                rowTypePrefix == ADDON_ROW_ID_PREFIX
            )
        ) {
            return true;
        }
        const nameCell = getNameFieldCellFromRow(row);
        if (!nameCell) {
            return true;
        }
        const searched = nameCell.text
            .toLowerCase()
            .includes(searchString.toLowerCase());
        if (searched) {
            visibleGroupsSet.add(nameCell.parentId);
        }
        return searched;
    });

    const categoryGroupsList = Array.from(visibleGroupsSet);
    categoryGroupsList.forEach((categoryRow) => {
        if (categoryRow.parentId) {
            visibleGroupsSet.add(categoryRow.parentId);
        }
    });
    const visibleLineAndGroupRows = searchedRows.filter((row, index) => {
        if (index == 0) {
            return true;
        }
        const [rowTypePrefix, groupId] = row.rowId.split("-");
        if (
            rowTypePrefix == LINE_ITEM_ROW_ID_PREFIX ||
            rowTypePrefix == ADDON_ROW_ID_PREFIX
        ) {
            return true;
        }
        return visibleGroupsSet.has(row.rowId);
    });
    return visibleLineAndGroupRows;
};

export const getChevronCellFromRow = (row) => {
    return row.cells.find(
        (cell) => cell.type == "chevron" || cell.renderer != null,
    ); // don't generalize what a cheveron row is
};

export const getNameFieldCellFromRow = (row) => {
    return row.cells.find((cell) => cell.columnId == "name");
};

export const rowsToHashTable = (rows) => {
    return Object.fromEntries(rows.map((row) => [row.rowId, row]));
};
